import { GQL_URL } from '@/lib/graphql/config';
import { clearAccessToken, getAccessToken, setAccessToken } from '../stores/auth-store';

// L'endpoint de refresh est REST (POST /auth/refresh), pas GraphQL : on
// dérive sa base de GQL_URL en retirant le suffixe /graphql.
const REFRESH_URL = `${GQL_URL.replace(/\/graphql\/?$/, '')}/auth/refresh`;

// fetch n'a aucun timeout par défaut : un refresh qui stalle au boot
// laisserait le bootstrap jamais settled → skeleton plein écran sans issue.
const REFRESH_TIMEOUT_MS = 10_000;

interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

// Single-flight : les appelants concurrents partagent la même promesse, donc
// UN appel réseau (S-03). Sans ça, un burst de 401 simultanés déclencherait N
// refresh — côté serveur un seul gagne le lock de rotation, les autres
// prendraient un 503. Relâchée dès qu'elle est settled.
let inflight: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  // Sur échec on ne purge QUE si le token n'a pas changé pendant l'appel :
  // un login concurrent (ex. pendant le silent refresh du boot) a pu poser
  // un token neuf et valide qu'il ne faut pas écraser.
  const tokenBefore = getAccessToken();
  const response = await fetch(REFRESH_URL, {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(REFRESH_TIMEOUT_MS),
  });
  if (!response.ok) {
    if (getAccessToken() === tokenBefore) clearAccessToken();
    throw new Error(`token refresh failed (${response.status})`);
  }
  const { accessToken } = (await response.json()) as RefreshResponse;
  setAccessToken(accessToken);
  return accessToken;
}

/**
 * Rafraîchit l'access token via le cookie httpOnly, en coalisant les appels
 * concurrents. Rejette (et purge le token en mémoire) si le refresh échoue.
 */
export function refreshAccessToken(): Promise<string> {
  inflight ??= doRefresh().finally(() => {
    inflight = null;
  });
  return inflight;
}
